import { message } from 'antd';
import { addCalComCalendar } from '@/api/calendars/calcom';
import { fetchAndUpdateWorkflowAnalysis, analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { ECalendar } from 'src/enums/ECalendar';
import { WorkflowType } from 'src/enums/EWorkflow';
import { ICalendarDeleteParams, ICalendarSetupParams } from '@/interfaces/ICalendarWorkflows';
import posthog from 'posthog-js';
import { EPostHogEvents } from 'src/enums/EPostHogEvents';
import { EPostHogProperties } from 'src/enums/EPostHogProperties';

export const handleCalendarSetup = async ({
	values,
	modalData,
	existingCalendars,
	selectedAgent,
	selectedAgentId,
	dispatch,
	onRefetch,
	onRefetchCalendars,
	closeModal,
}: ICalendarSetupParams) => {
	try {
		const newCalendar = {
			apiKey: values.apiKey,
			eventTypeId: values.eventTypeId,
			location: values.location,
			scenario: values.scenario || 'Whenever the caller wants to book or schedule any meetings.',
			sendCallerSMSNotification: values.sendCallerSMSNotification ?? true,
			sendOwnerSMSNotification: values.sendOwnerSMSNotification ?? true,
			nameOfCalendar:
				values.calendarType === 'gohighlevel' ? ECalendar.GOHIGHLEVEL_CALENDAR : ECalendar.CAL_COM,
			calendarSelected: values.calendarType === 'gohighlevel' ? values.calendarSelected : '',
			customFields: values.customFields || [],
			additionalUserInfoFields: values.additionalUserInfoFields,
		};

		const isEditMode = modalData && typeof modalData === 'object' && 'calendarId' in modalData;
		let updatedCalendars;

		if (isEditMode) {
			updatedCalendars = existingCalendars.map((cal) => {
				const isTargetCalendar = cal.calendarId === modalData.calendarId;

				if (isTargetCalendar) {
					return {
						...cal,
						...newCalendar,
						calendarId: cal.calendarId,
						...(cal.id && { id: cal.id }),
					};
				}
				return cal;
			});
		} else {
			updatedCalendars = [...existingCalendars, newCalendar];
		}

		await addCalComCalendar({
			calendars: updatedCalendars,
			username: selectedAgent?.number,
		});

		if (!isEditMode) {
			posthog.capture(EPostHogEvents.CALENDAR_WORKFLOW_CREATED, {
				[EPostHogProperties.AGENT_ID]: selectedAgentId,
				[EPostHogProperties.WORKFLOW_TYPE]: newCalendar.nameOfCalendar,
				[EPostHogProperties.EVENT_TYPE_ID]: newCalendar.eventTypeId,
			});
		}

		message.success(isEditMode ? 'Calendar updated successfully!' : 'Calendar added successfully!');

		closeModal?.();
		onRefetch();
		onRefetchCalendars?.();

		await analyzeAndUpdateWorkflow(
			{ calendar: updatedCalendars },
			WorkflowType.CALENDAR,
			dispatch,
			selectedAgentId,
		);
	} catch {
		message.error('Failed to configure calendar. Please try again.');
	}
};

export const handleDeleteCalendar = async ({
	calendar,
	existingCalendars,
	selectedAgent,
	selectedAgentId,
	dispatch,
	onRefetch,
	onRefetchCalendars,
}: ICalendarDeleteParams) => {
	try {
		const updatedCalendars = existingCalendars.filter(
			(cal: any) => cal.calendarId !== calendar.calendarId,
		);

		await addCalComCalendar({
			calendars: updatedCalendars,
			username: selectedAgent?.number,
		});

		message.success('Calendar deleted successfully!');
		onRefetch();
		onRefetchCalendars?.();
		await fetchAndUpdateWorkflowAnalysis(dispatch, selectedAgentId);
	} catch {
		message.error('Failed to delete calendar. Please try again.');
	}
};

export const createCalendarActionHandlers = (
	calendar: any,
	calendarWorkflows: any[],
	selectedAgent: any,
	selectedAgentId: string,
	dispatch: any,
	onRefetch: () => void,
	setIsDeleting: (isDeleting: boolean) => void,
	setShowEditModal: (show: boolean) => void,
	onRefetchCalendars?: () => void,
) => {
	const handleCalendarUpdate = async (values: any) => {
		await handleCalendarSetup({
			values,
			modalData: calendar,
			existingCalendars: calendarWorkflows,
			selectedAgent,
			selectedAgentId,
			dispatch,
			onRefetch,
			onRefetchCalendars,
			closeModal: () => setShowEditModal(false),
		});
	};

	const handleCalendarDelete = async () => {
		setIsDeleting(true);
		try {
			await handleDeleteCalendar({
				calendar,
				existingCalendars: calendarWorkflows,
				selectedAgent,
				selectedAgentId,
				dispatch,
				onRefetch,
				onRefetchCalendars,
			});
		} finally {
			setIsDeleting(false);
		}
	};

	return {
		handleCalendarUpdate,
		handleDeleteCalendar: handleCalendarDelete,
	};
};
